import { toLatinDigits, toPersianDigits } from '@clinic/money-date';
import { z } from 'zod';
import { IsoDateTimeSchema, OtpCodeSchema, PhoneInputSchema, UuidSchema } from './common.js';

export const OTP_CODE_LENGTH = 6;
export const OTP_TTL_SECONDS = 120;
export const OTP_MAX_ATTEMPTS = 5;

export const OtpRequestBodySchema = z.object({
  phone: PhoneInputSchema,
});
export type OtpRequestBody = z.input<typeof OtpRequestBodySchema>;

export const OtpRequestResponseSchema = z.object({
  challengeId: UuidSchema,
  expiresAt: IsoDateTimeSchema,
  resendAvailableAt: IsoDateTimeSchema,
});
export type OtpRequestResponse = z.infer<typeof OtpRequestResponseSchema>;

export const OtpVerifyBodySchema = z.object({
  challengeId: UuidSchema,
  code: OtpCodeSchema,
});
export type OtpVerifyBody = z.input<typeof OtpVerifyBodySchema>;

/**
 * Step-up: confirming the signed-in user again with a code sent to their own phone before a
 * sensitive action (bulk export). A login or a confirmation counts for this window.
 */
export const REAUTH_WINDOW_SECONDS = 600;

export const StepUpVerifyBodySchema = OtpVerifyBodySchema;
export type StepUpVerifyBody = z.input<typeof StepUpVerifyBodySchema>;

export const StepUpResponseSchema = z.object({
  reauthenticatedUntil: IsoDateTimeSchema,
});
export type StepUpResponse = z.infer<typeof StepUpResponseSchema>;

// ------------------------------------------------------------------ password login (ADR-033)

export const PASSWORD_MIN_LENGTH = 10;
export const PASSWORD_MAX_LENGTH = 128;
/** A password link is valid for a day and once. */
export const PASSWORD_LINK_TTL_HOURS = 24;

/**
 * What is hashed: NFKC, Persian and Arabic digits as Latin, Arabic yeh and kaf as Persian. A
 * password typed on a Persian or an English keyboard is then the same password.
 */
export function normalizePassword(value: string): string {
  return toLatinDigits(value.normalize('NFKC')).replace(/[يى]/g, 'ی').replace(/ك/g, 'ک');
}

const COMMON_PASSWORDS = new Set([
  '0123456789',
  '1234567890',
  '12345678910',
  '9876543210',
  '0987654321',
  '1q2w3e4r5t',
  'q1w2e3r4t5',
  'qwertyuiop',
  'qwerty1234',
  '1234qwer',
  'asdfghjkl',
  'password',
  'password1',
  'password12',
  'password123',
  'password1234',
  'iloveyou',
  'abcdefghij',
  'abcd123456',
  '123456789a',
  'a123456789',
  'admin12345',
  'clinic1234',
]);

/** Too easy to guess: one repeated character, a run of digits, or a well-known password. */
export function isTrivialPassword(normalized: string): boolean {
  const lower = normalized.toLowerCase();
  if (new Set(lower).size <= 2) return true;
  if (/^\d+$/.test(lower) && ('01234567890123456789'.includes(lower) || '98765432109876543210'.includes(lower))) {
    return true;
  }
  return COMMON_PASSWORDS.has(lower);
}

/** A new password (setting or changing). Login accepts any stored password; see PasswordInputSchema. */
export const NewPasswordSchema = z
  .string()
  .max(PASSWORD_MAX_LENGTH * 2, { message: 'رمز بیش از حد بلند است.' })
  .transform(normalizePassword)
  .pipe(
    z
      .string()
      .refine((v) => [...v].length >= PASSWORD_MIN_LENGTH, {
        message: `رمز باید دست‌کم ${toPersianDigits(PASSWORD_MIN_LENGTH)} نویسه باشد.`,
      })
      .refine((v) => [...v].length <= PASSWORD_MAX_LENGTH, { message: 'رمز بیش از حد بلند است.' })
      .refine((v) => !isTrivialPassword(v), { message: 'این رمز ساده و قابل حدس است. رمز دیگری انتخاب کنید.' }),
  );

/** A password typed to sign in or confirm: only normalized, never judged. */
export const PasswordInputSchema = z
  .string()
  .min(1, { message: 'رمز را وارد کنید.' })
  .max(PASSWORD_MAX_LENGTH * 2)
  .transform(normalizePassword);

export const PasswordLoginBodySchema = z.object({
  phone: PhoneInputSchema,
  password: PasswordInputSchema,
});
export type PasswordLoginBody = z.input<typeof PasswordLoginBodySchema>;

/** 32 random bytes, base64url. It travels in the URL fragment, never in a query string. */
export const PasswordLinkTokenSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/, { message: 'لینک معتبر نیست.' });

export const PASSWORD_LINK_PURPOSES = ['setup', 'reset'] as const;
export type PasswordLinkPurpose = (typeof PASSWORD_LINK_PURPOSES)[number];

export const PasswordLinkInspectBodySchema = z.object({ token: PasswordLinkTokenSchema });
export type PasswordLinkInspectBody = z.input<typeof PasswordLinkInspectBodySchema>;

export const PasswordLinkInspectResponseSchema = z.object({
  /** Enough for the holder to recognise their own number; the link may have been forwarded. */
  phoneLast4: z.string().regex(/^\d{4}$/),
  purpose: z.enum(PASSWORD_LINK_PURPOSES),
  expiresAt: IsoDateTimeSchema,
});
export type PasswordLinkInspectResponse = z.infer<typeof PasswordLinkInspectResponseSchema>;

export const PasswordLinkUseBodySchema = z.object({
  token: PasswordLinkTokenSchema,
  password: NewPasswordSchema,
});
export type PasswordLinkUseBody = z.input<typeof PasswordLinkUseBodySchema>;

/** Issued by a clinic admin for a member; the web turns it into `<origin>/set-password#<token>`. */
export const PasswordLinkResponseSchema = z.object({
  token: PasswordLinkTokenSchema,
  purpose: z.enum(PASSWORD_LINK_PURPOSES),
  expiresAt: IsoDateTimeSchema,
});
export type PasswordLinkResponse = z.infer<typeof PasswordLinkResponseSchema>;

export const PasswordChangeBodySchema = z.object({
  /** Required when the account has a password; an OTP-only account sets its first one. */
  currentPassword: PasswordInputSchema.optional(),
  newPassword: NewPasswordSchema,
});
export type PasswordChangeBody = z.input<typeof PasswordChangeBodySchema>;

export const StepUpPasswordBodySchema = z.object({ password: PasswordInputSchema });
export type StepUpPasswordBody = z.input<typeof StepUpPasswordBodySchema>;

/** Which sign-in methods this deployment offers (the SMS code only with an SMS operator). */
export const AuthMethodsResponseSchema = z.object({
  password: z.literal(true),
  otp: z.boolean(),
});
export type AuthMethodsResponse = z.infer<typeof AuthMethodsResponseSchema>;

export const AuthUserSchema = z.object({
  id: UuidSchema,
  phone: z.string(),
  displayName: z.string().nullable(),
  /** False until the person sets a password from a link (ADR-033). */
  hasPassword: z.boolean(),
});
export type AuthUser = z.infer<typeof AuthUserSchema>;

export const AuthTokenResponseSchema = z.object({
  accessToken: z.string(),
  accessTokenExpiresAt: IsoDateTimeSchema,
  user: AuthUserSchema,
});
export type AuthTokenResponse = z.infer<typeof AuthTokenResponseSchema>;

export const SessionSchema = z.object({
  id: UuidSchema,
  createdAt: IsoDateTimeSchema,
  lastUsedAt: IsoDateTimeSchema.nullable(),
  expiresAt: IsoDateTimeSchema,
  userAgent: z.string().nullable(),
  current: z.boolean(),
});
export type SessionView = z.infer<typeof SessionSchema>;

export const SessionListResponseSchema = z.object({
  items: z.array(SessionSchema),
});
export type SessionListResponse = z.infer<typeof SessionListResponseSchema>;
